import type * as React from "react";

import { cn } from "../cn";

// CHART-DIMENSIONS (2026-10-21; KPI Beast's seat card, in the DTP look): the compact rows a
// seat card carries under its identity, one per dimension — a 12 px label column on the left,
// the value on the right, 20 px tall, hairline between the identity and the first row. The
// legend list is the same rows for the canvas's legend popover: a swatch, a label, a note.

export type DimensionRowProps = Omit<React.ComponentProps<"div">, "children"> & {
  /** The dimension's key (`data-dimension`, which tests and the legend read). */
  dimension: string;
  label: React.ReactNode;
  children: React.ReactNode;
  /** `muted`: nothing to show ("Not scored", "No cost"); `warning` / `danger`: the value's tone. */
  tone?: "default" | "muted" | "warning" | "danger";
  /** CHART-KB: `compact` is the card's row — 11 px text, a 16 px line, a 64 px label; the value wraps inside the card. */
  density?: "default" | "compact";
};

export function DimensionRow({
  dimension,
  label,
  tone = "default",
  density = "default",
  className,
  children,
  ...props
}: DimensionRowProps) {
  const compact = density === "compact";
  return (
    <div
      data-slot="dimension-row"
      data-dimension={dimension}
      data-tone={tone}
      className={cn(
        "flex min-w-0 items-start gap-2",
        compact ? "min-h-4 text-2xs leading-4" : "min-h-5 text-xs leading-5",
        tone === "muted" && "text-faint",
        tone === "warning" && "text-warning-text",
        tone === "danger" && "text-danger-text",
        className,
      )}
      {...props}
    >
      {/* Wave 9: `label={null}` is an item row (a measurable, a priority, a missing item) that
          takes the card's whole width, KPI Beast's way; "" keeps the column for alignment. */}
      {label === null ? null : (
        <span className={cn("shrink-0 truncate text-faint", compact ? "w-16" : "w-[72px]")}>
          {label}
        </span>
      )}
      <span
        className={cn(
          "flex min-w-0 flex-1 items-center gap-x-1.5 gap-y-0.5",
          compact ? "flex-wrap overflow-hidden" : "flex-wrap",
        )}
      >
        {children}
      </span>
    </div>
  );
}

/** The rows of a card, under a hairline. */
export function DimensionRows({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dimension-rows"
      className={cn("flex min-w-0 flex-col gap-0.5 border-t border-subtle pt-1.5", className)}
      {...props}
    />
  );
}

export type LegendItemProps = Omit<React.ComponentProps<"li">, "children"> & {
  /** The swatch: a band token class (`bg-band-great`), a line kind, or any element. */
  swatch: React.ReactNode;
  label: React.ReactNode;
  note?: React.ReactNode;
};

export function LegendItem({ swatch, label, note, className, ...props }: LegendItemProps) {
  return (
    <li
      data-slot="legend-item"
      className={cn("flex min-w-0 items-center gap-2 text-xs leading-5", className)}
      {...props}
    >
      <span className="flex size-4 shrink-0 items-center justify-center" aria-hidden="true">
        {swatch}
      </span>
      <span className="truncate text-foreground">{label}</span>
      {note ? <span className="ml-auto shrink-0 pl-3 text-faint">{note}</span> : null}
    </li>
  );
}

/** A legend's section: a 11 px caps label over its items. */
export function LegendSection({
  title,
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & { title: React.ReactNode }) {
  return (
    <div data-slot="legend-section" className={cn("flex flex-col gap-1", className)} {...props}>
      <span className="text-[11px] leading-4 font-medium tracking-wide text-faint uppercase">
        {title}
      </span>
      <ul className="flex flex-col gap-0.5">{children}</ul>
    </div>
  );
}

/** A legend swatch: a 10 px square in a tone class (`bg-band-great`, `bg-warning`). */
export function Swatch({
  className,
  shape = "square",
  ...props
}: React.ComponentProps<"span"> & { shape?: "square" | "line" | "dashed" }) {
  return (
    <span
      data-slot="swatch"
      className={cn(
        shape === "square" && "size-2.5 rounded-[2px]",
        shape === "line" && "h-0.5 w-4 rounded-full",
        shape === "dashed" && "size-3 rounded-[3px] border border-dashed border-strong",
        className,
      )}
      {...props}
    />
  );
}
